"use client"

import useApp from '@/lib/store'
import { Environment, PerspectiveCamera, Text3D } from '@react-three/drei'
import { Canvas, useFrame } from '@react-three/fiber'
import { useTheme } from 'next-themes'
import { Suspense, useMemo, useRef } from 'react'
import * as THREE from 'three'

const FloatingText = ({ position, rotation, char, font }: { position: THREE.Vector3, rotation: number, char: string, font: string }) => {
	const {isLoading, setIsLoading} = useApp()
	const { theme } = useTheme()
	const isDarkMode = theme === 'dark'
	const meshRef = useRef<THREE.Mesh>(null)

	const rotationSpeed = useMemo(() => ({
		x: Math.random() * 0.2 + 0.1,
		y: Math.random() * 0.3 + 0.1,
		z: Math.random() * 0.1 + 0.05
	}), [])

	// Memoize material properties instead of creating a new material instance
	const materialProps = useMemo(() => ({
		color: !isDarkMode ? "white" : "#000000",
		metalness: 0.8,
		roughness: 0.2,
		clearcoat: 1.0,
		clearcoatRoughness: 0.2,
		reflectivity: 0.5,
		envMapIntensity: 0.4,
		transmission: 0.2,
		thickness: 0.5,
		attenuationColor: !isDarkMode ? "#eeeeee" : "#222222",
	}), [isDarkMode])

	useFrame((state, delta) => {
		if (isLoading) {
			setIsLoading(false)
		}
		if (meshRef.current) {
			meshRef.current.rotation.x += delta * rotationSpeed.x
			meshRef.current.rotation.y += delta * rotationSpeed.y
			meshRef.current.rotation.z += delta * rotationSpeed.z
		}
	})

	return (
		<group position={position} rotation={[0, rotation, 0]}>
			<mesh ref={meshRef} castShadow receiveShadow frustumCulled>
				<Text3D
					font={font}
					size={1.5}
					height={0.5}
					curveSegments={128}
					bevelEnabled
					bevelThickness={0.05}
					bevelSize={0.02}
					bevelSegments={4}
					material={new THREE.MeshPhysicalMaterial(materialProps)}
				>
					{char}
				</Text3D>
			</mesh>
		</group>
	)
}

const FloatingText3D = ({ font, characters }: { font: string, characters: string }) => {
	const {isLoading} = useApp()
	const { theme } = useTheme()
	const isDarkMode = theme === 'dark'

	const chars = useMemo(() => {
		const elements = []
		const positions = []
		const rotations = []
		const charSize = new THREE.Vector3(1.5, 1.5, 0.5)
		const bufferSize = 0.1
		const sceneSize = 5

		const isOverlapping = (newPos: number[]) => {
			return positions.some(pos =>
				Math.abs(newPos[0] - pos[0]) < charSize.x + bufferSize &&
				Math.abs(newPos[1] - pos[1]) < charSize.y + bufferSize &&
				Math.abs(newPos[2] - pos[2]) < charSize.z + bufferSize
			)
		}

		const generatePosition = () => {
			const angle = Math.random() * Math.PI * 2
			const radius = Math.random() * (sceneSize / 2 - charSize.x) + charSize.x
			const x = Math.cos(angle) * radius
			const y = Math.sin(angle) * radius
			const z = (Math.random() - 0.5) * sceneSize
			return [x, y, z]
		}

		for (let i = 0; i < characters.length; i++) {
			let position
			let attempts = 0
			const maxAttempts = 1000

			do {
				position = generatePosition()
				attempts++
			} while (isOverlapping(position) && attempts < maxAttempts)

			if (attempts === maxAttempts) {
				console.warn('Could not find non-overlapping position for character', characters[i])
				continue
			}

			positions.push(position)
			rotations.push(Math.random() * 360)
			elements.push(
				<FloatingText key={i} position={position} rotation={rotations[i]} char={characters[i]} font={font} />
			)
		}

		return elements
	}, [characters, font])

	return (
		<div className="relative w-full h-lvh">
			<Canvas
				shadows
				dpr={[1, 2]}
				gl={{ antialias: true }}
			>
				<Suspense fallback={null}>
					{/* <color attach="background" args={[!isDarkMode ? "white" : "black"]} /> */}
					<Environment preset="studio" />
					<PerspectiveCamera makeDefault position={[0, 0, 5]} fov={45} />
					<ambientLight intensity={5} color={!isDarkMode ? "white" : "black"} />
					<directionalLight
						position={[0, 0, 5]}
						intensity={5}
						color={!isDarkMode ? "white" : "black"}
						castShadow
						shadow-mapSize-width={2048}
						shadow-mapSize-height={2048}
					/>
					<directionalLight
						position={[-5, 0, 5]}
						intensity={2.5}
						color={!isDarkMode ? "white" : "black"}
						castShadow
						shadow-mapSize-width={2048}
						shadow-mapSize-height={2048}
					/>
					{chars}
				</Suspense>
			</Canvas>
			{!isLoading && <div className='fixed w-full h-lvh z-600 inset-0 pointer-events-none bg-white dark:bg-black animate-fade-out' />}
		</div>
	)
}

export default FloatingText3D

